import type React from 'react';
import { useCallback } from 'react';
import type { FormField } from '../../form-types';
import type { BaseShaderConfig } from '../../base-config';
import { GeometricShader } from '../../geometric-shader';
import {
  ShaderBehavior,
  ShaderCategory,
  type ShaderMetadata,
} from '../../shader-types';
import { useShaderPreview } from '../../hooks/useShaderPreview';

export interface SpinningCubeShaderProps extends BaseShaderConfig {
  rotationSpeed: number;
  cubeColor: string;
  backgroundColor: string;
}

export class SpinningCubeShader extends GeometricShader {
  name = 'spinningcube' as const;
  rotationSpeed!: number;
  cubeColor!: string;
  backgroundColor!: string;
  width!: number;
  height!: number;
  referenceWidth!: number;
  referenceHeight!: number;

  constructor(props: Partial<SpinningCubeShaderProps> = {}) {
    super();

    // Set defaults
    this.name = 'spinningcube';
    this.rotationSpeed = 1.0;
    this.cubeColor = '#00ff88';
    this.backgroundColor = '#000000';
    this.width = 512;
    this.height = 512;
    this.referenceWidth = 512;
    this.referenceHeight = 512;

    // Apply provided props
    Object.assign(this, props);
  }

  generate(): string {
    return `
      precision mediump float;
      ${this.getGeometricUniforms()}
      uniform float u_rotation_speed;

      // Rotation matrices
      mat3 rotateX(float angle) {
        float c = cos(angle);
        float s = sin(angle);
        return mat3(
          1.0, 0.0, 0.0,
          0.0, c, -s,
          0.0, s, c
        );
      }

      mat3 rotateY(float angle) {
        float c = cos(angle);
        float s = sin(angle);
        return mat3(
          c, 0.0, s,
          0.0, 1.0, 0.0,
          -s, 0.0, c
        );
      }

      mat3 rotateZ(float angle) {
        float c = cos(angle);
        float s = sin(angle);
        return mat3(
          c, -s, 0.0,
          s, c, 0.0,
          0.0, 0.0, 1.0
        );
      }

      // Project 3D point to 2D screen
      vec2 project(vec3 point) {
        float perspective = 1.0 / (1.0 + point.z * 0.3);
        return point.xy * perspective;
      }

      // Distance to line segment
      float distToLine(vec2 p, vec2 a, vec2 b) {
        vec2 pa = p - a;
        vec2 ba = b - a;
        float h = clamp(dot(pa, ba) / dot(ba, ba), 0.0, 1.0);
        return length(pa - ba * h);
      }

      void main() {
        ${this.getGeometricUVPreamble()}

        // Initial 45-degree rotation on each axis + spinning
        float time = u_time * u_rotation_speed;
        mat3 rotation = rotateX(0.785398 + time) * rotateY(0.785398 + time) * rotateZ(0.785398 + time);

        // Cube vertices (8 corners)
        vec3 vertices[8];
        vertices[0] = vec3(-1.0, -1.0, -1.0); // 0
        vertices[1] = vec3( 1.0, -1.0, -1.0); // 1
        vertices[2] = vec3( 1.0,  1.0, -1.0); // 2
        vertices[3] = vec3(-1.0,  1.0, -1.0); // 3
        vertices[4] = vec3(-1.0, -1.0,  1.0); // 4
        vertices[5] = vec3( 1.0, -1.0,  1.0); // 5
        vertices[6] = vec3( 1.0,  1.0,  1.0); // 6
        vertices[7] = vec3(-1.0,  1.0,  1.0); // 7

        // Transform and project vertices
        vec2 projectedVertices[8];
        for(int i = 0; i < 8; i++) {
          vec3 transformed = rotation * (vertices[i] * 0.6);
          projectedVertices[i] = project(transformed);
        }

        float minDist = 1000.0;

        // Draw all 12 edges of the cube
        // Front face
        minDist = min(minDist, distToLine(uv, projectedVertices[0], projectedVertices[1]));
        minDist = min(minDist, distToLine(uv, projectedVertices[1], projectedVertices[2]));
        minDist = min(minDist, distToLine(uv, projectedVertices[2], projectedVertices[3]));
        minDist = min(minDist, distToLine(uv, projectedVertices[3], projectedVertices[0]));

        // Back face
        minDist = min(minDist, distToLine(uv, projectedVertices[4], projectedVertices[5]));
        minDist = min(minDist, distToLine(uv, projectedVertices[5], projectedVertices[6]));
        minDist = min(minDist, distToLine(uv, projectedVertices[6], projectedVertices[7]));
        minDist = min(minDist, distToLine(uv, projectedVertices[7], projectedVertices[4]));

        // Connecting edges
        minDist = min(minDist, distToLine(uv, projectedVertices[0], projectedVertices[4]));
        minDist = min(minDist, distToLine(uv, projectedVertices[1], projectedVertices[5]));
        minDist = min(minDist, distToLine(uv, projectedVertices[2], projectedVertices[6]));
        minDist = min(minDist, distToLine(uv, projectedVertices[3], projectedVertices[7]));

        // Create wireframe effect with anti-aliasing
        float lineWidth = 0.03;
        float feather = 0.01; // Anti-aliasing feather
        float alpha = 1.0 - smoothstep(lineWidth - feather, lineWidth + feather, minDist);

        // Add depth-based line intensity
        float avgDepth = 0.0;
        for(int i = 0; i < 8; i++) {
          vec3 transformed = rotation * (vertices[i] * 0.6);
          avgDepth += transformed.z;
        }
        avgDepth /= 8.0;
        float depthFactor = 0.7 + 0.3 * (avgDepth + 1.0) / 2.0;

        vec3 cubeCol = vec3(${this.parseColor(this.cubeColor)}) * depthFactor;
        vec3 bgCol = vec3(${this.parseColor(this.backgroundColor)});

        vec3 color = mix(bgCol, cubeCol, alpha);
        gl_FragColor = vec4(color, 1.0);
      }
    `;
  }
  private parseColor(hex: string): string {
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;
    return `${r.toFixed(3)}, ${g.toFixed(3)}, ${b.toFixed(3)}`;
  }

  static getFormConfig(): FormField[] {
    return [
      {
        type: 'range',
        name: 'rotationSpeed',
        label: 'Rotation Speed',
        description: 'Speed of cube rotation',
        min: 0.1,
        max: 3.0,
        step: 0.1,
        defaultValue: 1.0,
        colspan: 2, // Full width for the main control
      },
      {
        type: 'color',
        name: 'cubeColor',
        label: 'Cube Color',
        description: 'Color of the cube wireframe',
        defaultValue: '#00ff88',
        colspan: 1,
      },
      {
        type: 'color',
        name: 'backgroundColor',
        label: 'Background Color',
        description: 'Background color',
        defaultValue: '#000000',
        colspan: 1,
      },
    ];
  }

  static getMetadata(): ShaderMetadata {
    return {
      behavior: ShaderBehavior.GEOMETRIC,
      category: ShaderCategory.OBJECTS,
      description: 'Rotating wireframe cube with customizable colors and speed',
      tags: ['cube', 'wireframe', '3d', 'rotation', 'geometric'],
    };
  }
}

// Preview Component
export const SpinningCubePreview: React.FC<{ model: SpinningCubeShader }> = ({
  model,
}) => {
  const getUniformNames = useCallback(() => ['u_rotation_speed'], []);

  const updateUniforms = useCallback(
    (
      gl: WebGLRenderingContext,
      locations: Record<string, WebGLUniformLocation | null>,
      model: SpinningCubeShader
    ) => {
      gl.uniform1f(locations.u_rotation_speed, model.rotationSpeed);
    },
    []
  );

  const canvasRef = useShaderPreview(model, getUniformNames, updateUniforms);

  return (
    <canvas
      ref={canvasRef}
      width={model.width}
      height={model.height}
      style={{ maxWidth: '100%', height: 'auto' }}
    />
  );
};
